// components/GetAsignaturas.jsx
import React, { FC, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { get_asignaturas } from "../services/api";
import { UseSetLenguajePorPais } from "../hooks/UseSetLenguajePorPais";
import { useAuthContext } from "../hooks/Auth";
import { useTranslation } from "react-i18next";
import { FormattedAsignatura } from "../interfaces/ObjetivosAprendizaje/iAsignaturas";

interface Props {
  nivelId: any;
  onAsignaturaChange: (asignatura: FormattedAsignatura) => void;
  selectedAsignatura: string;
}

const GetAsignaturas: FC<Props> = ({
  nivelId,
  onAsignaturaChange,
  selectedAsignatura,
}) => {
  const [data, setData] = useState<FormattedAsignatura[]>([]);
  const { user } = useAuthContext();
  const { t } = useTranslation();

  const IS_FROM_URUGUAY = user?.pais === "UY";

  // Hook para establecer el lenguaje según el país
  UseSetLenguajePorPais();

  useEffect(() => {
    async function fetchAsignaturas() {
      // Validar que el país provenga del contexto y sea uno de los permitidos
      const paisPermitidos = ["CL", "PE", "ARG", "UY"];
      if (!user?.pais || !paisPermitidos.includes(user.pais)) {
        console.error(
          "El país no es válido. Se requiere un país válido: CL o PE."
        );
        return;
      }
      if (!nivelId) {
        // Si no hay un nivel seleccionado, no se realiza la consulta
        return;
      }
      try {
        const { data: responseData, status } = await get_asignaturas(
          user.pais,
          nivelId
        );
        if (status === 200) {
          // La respuesta contiene en "body" el listado de asignaturas
          // Se mapea para obtener un array de objetos con id y nombre
          const asignaturasArray: FormattedAsignatura[] = responseData.body.map(
            (asignatura) => ({
              id: asignatura.id_asignatura,
              nombre: asignatura.name,
            })
          );
          setData(asignaturasArray);
        } else {
          console.error("La solicitud a la API no fue exitosa.");
        }
      } catch (error) {
        console.error(
          "Se produjo un error al realizar la solicitud a la API:",
          error
        );
      }
    }

    fetchAsignaturas();
  }, [user?.pais, nivelId]);

  const handleAsignaturaChange = (event: SelectChangeEvent<any>) => {
    const selectedId = event.target.value;
    const asignaturaSeleccionada = data.find((item) => item.id === selectedId);
    onAsignaturaChange(asignaturaSeleccionada as FormattedAsignatura);
    console.log("Asignatura seleccionada:", asignaturaSeleccionada);
  };

  return (
    <Grid item xs={12}>
      <Box>
        <Typography
          sx={{
            fontFamily: "montserrat",
            fontSize: "1.25rem",
            fontWeight: "bold",
            color: "#336B87",
          }}
        >
          {!IS_FROM_URUGUAY ? t("asignatura") : "Materia *"}
        </Typography>
        <FormControl fullWidth>
          <Select
            labelId="select-asignatura"
            id="select-asignatura"
            variant="outlined"
            value={selectedAsignatura || ""}
            displayEmpty
            onChange={handleAsignaturaChange}
            sx={{
              fontFamily: "montserrat",
              fontSize: "14px",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& fieldset": { borderColor: "#BDBDBD" },
              "&:hover fieldset": { borderColor: "#9E9E9E" },
              "&.Mui-focused fieldset": { borderColor: "#005559" },
              "& .MuiSelect-select": {
                color: selectedAsignatura ? "#005559" : "#B3D1D1",
              },
            }}
          >
            <option hidden value="">
              Selecciona por ejemplo: Matemáticas
            </option>
            {data.map((asignatura) => (
              <MenuItem
                key={asignatura.id}
                value={asignatura.id}
                sx={{ color: "#005559" }}
              >
                <Typography
                  sx={{
                    fontFamily: "montserrat",
                    fontSize: "0.8rem",
                    color: "#336B87",
                  }}
                >
                  {asignatura.nombre}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Grid>
  );
};

export default GetAsignaturas;
